/**
 * Deciding what to fetch, in what order, and what to throw away.
 *
 * This module is deliberately free of network, DOM and engine types: it drives
 * an abstract `TileLoader` and reports through callbacks, so the whole streaming
 * policy is unit-testable with a fake loader and no clock.
 */

import type { LodConfig } from '../../config.js';
import type { GeoOrigin } from '../geo/enu.js';
import type { TileKey } from '../geo/tilekey.js';
import { tileKeyToString } from '../geo/tilekey.js';
import { LruCache } from './lru.js';
import type { DesiredTile, ViewerState } from './lod.js';
import { computeDesiredTiles } from './lod.js';

export interface TileLoader<T> {
  /** Reject with an `AbortError` when `signal` fires; the scheduler ignores those. */
  load(tile: DesiredTile, signal: AbortSignal): Promise<T>;
}

export interface SchedulerEvents<T> {
  /** A tile became resident and should be drawn. */
  onReady(tile: DesiredTile, payload: T): void;
  /** A tile is no longer wanted and should stop being drawn. */
  onDropped(key: TileKey): void;
  /** Loading failed for a reason other than cancellation. */
  onError?(key: TileKey, error: unknown): void;
}

export interface SchedulerOptions<T> {
  loader: TileLoader<T>;
  events: SchedulerEvents<T>;
  concurrency: number;
  memoryBudgetBytes: number;
  sizeOf: (payload: T) => number;
  /** Released when a cached payload is finally evicted. */
  onEvict?: (key: string, payload: T) => void;
}

export interface SchedulerStats {
  active: number;
  pending: number;
  inFlight: number;
  cached: number;
  cacheBytes: number;
  failed: number;
}

export class TileScheduler<T> {
  private readonly options: SchedulerOptions<T>;
  private readonly cache: LruCache<T>;

  /** Currently wanted, keyed by `z/x/y`. Replaced wholesale on each update. */
  private desired = new Map<string, DesiredTile>();
  /** Wanted, resident and handed to the renderer. */
  private readonly active = new Map<string, DesiredTile>();
  private readonly inFlight = new Map<string, AbortController>();
  /** Keys that errored, so a single bad tile is not retried every frame. */
  private readonly failed = new Set<string>();
  private disposed = false;

  constructor(options: SchedulerOptions<T>) {
    this.options = options;
    this.cache = new LruCache<T>({
      maxBytes: options.memoryBudgetBytes,
      sizeOf: options.sizeOf,
      onEvict: options.onEvict,
    });
  }

  get stats(): SchedulerStats {
    let pending = 0;
    for (const key of this.desired.keys()) {
      if (!this.active.has(key) && !this.inFlight.has(key) && !this.failed.has(key)) pending++;
    }
    return {
      active: this.active.size,
      pending,
      inFlight: this.inFlight.size,
      cached: this.cache.size,
      cacheBytes: this.cache.bytes,
      failed: this.failed.size,
    };
  }

  /** Recompute what should be resident and start converging on it. */
  update(viewer: ViewerState, lod: LodConfig, origin: GeoOrigin): void {
    if (this.disposed) return;
    this.desired = computeDesiredTiles(viewer, lod, origin);

    // Stop drawing anything that left the set. The payload stays in the cache,
    // so stepping back the way you came costs nothing.
    for (const key of [...this.active.keys()]) {
      if (this.desired.has(key)) continue;
      const tile = this.active.get(key)!;
      this.active.delete(key);
      this.cache.unpin(key);
      this.options.events.onDropped(tile.key);
    }

    // Cancel work nobody is waiting for any more. Without this, walking
    // quickly leaves a long tail of fetches for ground already behind you,
    // starving the tiles ahead.
    for (const [key, controller] of [...this.inFlight]) {
      if (!this.desired.has(key)) {
        controller.abort();
        this.inFlight.delete(key);
      }
    }

    // A tile that left and came back is no longer a failure worth suppressing.
    for (const key of [...this.failed]) {
      if (!this.desired.has(key)) this.failed.delete(key);
    }

    // Anything already decoded can go straight back on screen.
    for (const [key, tile] of this.desired) {
      if (this.active.has(key) || this.inFlight.has(key)) continue;
      const cached = this.cache.get(key);
      if (cached !== undefined) this.activate(key, tile, cached);
    }

    this.pump();
  }

  /** Start loads up to the concurrency limit, nearest tile first. */
  private pump(): void {
    while (this.inFlight.size < this.options.concurrency) {
      const next = this.nextPending();
      if (next === undefined) return;
      this.begin(next);
    }
  }

  /**
   * The closest tile still needing work.
   *
   * A linear scan rather than a priority queue: the desired set is ~100 tiles
   * and every entry's priority changes whenever the player moves, so a heap
   * would be rebuilt as often as it was queried.
   */
  private nextPending(): DesiredTile | undefined {
    let best: DesiredTile | undefined;
    let bestKey = '';
    for (const [key, tile] of this.desired) {
      if (this.active.has(key) || this.inFlight.has(key) || this.failed.has(key)) continue;
      if (best === undefined || tile.distance < best.distance) {
        best = tile;
        bestKey = key;
      }
    }
    return best === undefined ? undefined : { ...best, key: this.desired.get(bestKey)!.key };
  }

  private begin(tile: DesiredTile): void {
    const key = tileKeyToString(tile.key);
    const controller = new AbortController();
    this.inFlight.set(key, controller);

    this.options.loader.load(tile, controller.signal).then(
      (payload) => {
        this.inFlight.delete(key);
        if (this.disposed || controller.signal.aborted) return;
        // The player may have moved on while this was in flight. Keep the
        // payload cached regardless, but only draw it if it is still wanted.
        const current = this.desired.get(key);
        const wanted = current !== undefined && !this.active.has(key);
        // Pin as part of the insert when we are about to draw it, so a full
        // cache cannot discard the tile between storing and pinning.
        this.cache.set(key, payload, { pin: wanted });
        if (wanted) this.activate(key, current, payload);
        this.pump();
      },
      (error: unknown) => {
        this.inFlight.delete(key);
        if (this.disposed || controller.signal.aborted || isAbort(error)) return;
        this.failed.add(key);
        this.options.events.onError?.(tile.key, error);
        this.pump();
      },
    );
  }

  private activate(key: string, tile: DesiredTile, payload: T): void {
    this.active.set(key, tile);
    // Pin before handing over: from here until it is dropped, the renderer
    // holds this payload and it must survive any amount of cache pressure.
    this.cache.pin(key);
    this.options.events.onReady(tile, payload);
  }

  /**
   * Load a resident tile again and swap the new payload in when it lands —
   * for when the data behind a tile got better after it was built. The old
   * payload stays on screen until then, so nothing blinks out. A tile that is
   * cached but not drawn is simply forgotten, so its next visit reloads it.
   */
  refresh(key: TileKey): void {
    const id = tileKeyToString(key);
    if (this.disposed || this.inFlight.has(id)) return;
    const tile = this.active.get(id);
    if (tile === undefined) {
      this.cache.delete(id);
      return;
    }
    const controller = new AbortController();
    this.inFlight.set(id, controller);
    this.options.loader.load(tile, controller.signal).then(
      (payload) => {
        this.inFlight.delete(id);
        if (this.disposed || controller.signal.aborted) return;
        if (this.active.has(id)) {
          this.active.delete(id);
          this.options.events.onDropped(tile.key);
        }
        const current = this.desired.get(id);
        // Replacing the entry evicts the old payload through `onEvict`.
        this.cache.set(id, payload, { pin: current !== undefined });
        if (current !== undefined) this.activate(id, current, payload);
        this.pump();
      },
      () => {
        // The old payload is still good; keep it.
        this.inFlight.delete(id);
        this.pump();
      },
    );
  }

  /** Forget a failed tile so the next update retries it. */
  retry(key: TileKey): void {
    this.failed.delete(tileKeyToString(key));
  }

  dispose(): void {
    this.disposed = true;
    for (const controller of this.inFlight.values()) controller.abort();
    this.inFlight.clear();
    this.active.clear();
    this.desired.clear();
    this.cache.clear();
  }
}

function isAbort(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}
